/**
 * Window utility functions for consistent mobile detection and sizing
 */

// Standard breakpoints matching your project's CSS
export const BREAKPOINTS = {
  MOBILE: 640,
  TABLET: 1024,
  DESKTOP: 1025,
};

/**
 * Get window dimensions safely (SSR-compatible)
 * @returns {[number, number]} - [width, height]
 */
export const getWindowSize = () => {
  if (typeof window === "undefined") {
    return [1200, 800]; // Default fallback for SSR
  }

  // Use window dimensions instead of document.body for more reliable sizing
  return [window.innerWidth, window.innerHeight];
};

/**
 * Check if current screen size matches mobile breakpoint
 * @returns {boolean} - True if mobile
 */
export const isMobile = () => {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= BREAKPOINTS.MOBILE;
};

/**
 * Check if current screen size matches tablet breakpoint
 * @returns {boolean} - True if tablet
 */
export const isTablet = () => {
  if (typeof window === "undefined") return false;
  const width = window.innerWidth;
  return width > BREAKPOINTS.MOBILE && width <= BREAKPOINTS.TABLET;
};

/**
 * Check if current screen size matches desktop breakpoint
 * @returns {boolean} - True if desktop
 */
export const isDesktop = () => {
  if (typeof window === "undefined") return false;
  return window.innerWidth >= BREAKPOINTS.DESKTOP;
};

/**
 * Get device orientation
 * @returns {string} - 'landscape' or 'portrait'
 */
export const getOrientation = () => {
  if (typeof window === "undefined") return "landscape";
  return window.innerWidth > window.innerHeight ? "landscape" : "portrait";
};

/**
 * Check if device supports touch
 * @returns {boolean} - True if touch device
 */
export const isTouchDevice = () => {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

/**
 * Get the most appropriate device type based on screen size and capabilities
 * @returns {string} - 'mobile', 'tablet', or 'desktop'
 */
export const getDeviceType = () => {
  if (isMobile()) return "mobile";
  if (isTablet()) return "tablet";
  return "desktop";
};
